import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CompetencyMatrixForm from "../components/fase2/CompetencyMatrixForm";
import { fetchV2 } from "../utils/apiV2";
import "./Fase2Preview.css";

// Página nueva y AISLADA para probar el formulario dinámico Fase 2
// (docs/arquitectura-objetivo.md §7, paso 9 del orden obligatorio). No
// reemplaza /performance ni /performance-evaluation-boss — esos siguen
// exactamente iguales. Esta ruta no aparece en ningún menú de navegación
// (Header.js no la referencia); solo es alcanzable escribiendo la URL
// directamente, a propósito, mientras Fase 2 no tenga un flujo de negocio
// decidido de principio a fin (asignación de "pares", activación de
// períodos, etc. — ver docs/matriz-trazabilidad.md, decisiones pendientes).
//
// Modo de uso:
//   /fase2-preview?banco=EST&fuente=jefe
//     -> Carga el banco real y permite calificar en el navegador. Si además
//        se pasa &asignacion=123, los cambios se pueden enviar de verdad
//        contra POST api/v2/asignaciones/123/calificaciones (Etapa 7).
//        Sin ese parámetro, queda en modo "solo vista previa": el estado
//        se mantiene en el navegador pero nunca se envía al backend.

export default function Fase2Preview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const codigoBanco = searchParams.get("banco") || "";
  const fuente = searchParams.get("fuente") || "jefe";
  const idAsignacion = searchParams.get("asignacion") || "";

  const [inputBanco, setInputBanco] = useState(codigoBanco);
  const [inputFuente, setInputFuente] = useState(fuente);
  const [inputAsignacion, setInputAsignacion] = useState(idAsignacion);

  const [estadoFormulario, setEstadoFormulario] = useState(null);
  const [envioEstado, setEnvioEstado] = useState("idle"); // idle | enviando | ok | error
  const [envioMensaje, setEnvioMensaje] = useState("");

  function aplicarSeleccion(e) {
    e.preventDefault();
    const nuevosParams = { banco: inputBanco.trim(), fuente: inputFuente };
    if (inputAsignacion.trim()) {
      nuevosParams.asignacion = inputAsignacion.trim();
    }
    setSearchParams(nuevosParams);
  }

  async function enviarCalificaciones() {
    if (!idAsignacion || !estadoFormulario) return;

    setEnvioEstado("enviando");
    setEnvioMensaje("");

    const pendientes = estadoFormulario.calificaciones.filter((c) => c.valor);
    let errores = 0;

    for (const calificacion of pendientes) {
      const { ok, data } = await fetchV2(`api/v2/asignaciones/${idAsignacion}/calificaciones`, {
        method: "POST",
        body: JSON.stringify({
          id_comportamiento: calificacion.id_comportamiento,
          valor: calificacion.valor,
          justificacion: calificacion.justificacion || null,
          evidencia_texto: calificacion.evidenciaTexto || null,
        }),
      });
      if (!ok) {
        errores += 1;
        setEnvioMensaje((prev) => `${prev}\n${(data && data.message) || "Error desconocido"}`.trim());
      }
    }

    setEnvioEstado(errores === 0 ? "ok" : "error");
    if (errores === 0) {
      setEnvioMensaje(`Se guardaron ${pendientes.length} calificación(es) correctamente.`);
    }
  }

  return (
    <div className="fase2-preview-page">
      <div className="fase2-preview-header">
        <h1>Vista previa — Formulario dinámico Fase 2</h1>
        <p className="fase2-preview-nota">
          Página de prueba, no enlazada desde ningún menú. Consume la configuración real de
          bancos/competencias/comportamientos (Etapa 7) — no reemplaza los formularios actuales.
        </p>
      </div>

      <form className="fase2-preview-selector" onSubmit={aplicarSeleccion}>
        <label>
          Banco
          <input
            value={inputBanco}
            onChange={(e) => setInputBanco(e.target.value.toUpperCase())}
            placeholder="EST, TAC, TEC, TEC-L, OPA, OP-CAMPO"
          />
        </label>
        <label>
          Fuente
          <select value={inputFuente} onChange={(e) => setInputFuente(e.target.value)}>
            <option value="auto">auto</option>
            <option value="jefe">jefe</option>
            <option value="par">par</option>
            <option value="colaborador">colaborador</option>
          </select>
        </label>
        <label>
          id de asignación (opcional, para poder guardar de verdad)
          <input
            value={inputAsignacion}
            onChange={(e) => setInputAsignacion(e.target.value)}
            placeholder="vacío = solo vista previa, no se guarda nada"
          />
        </label>
        <button type="submit">Cargar</button>
      </form>

      {codigoBanco && (
        <>
          <CompetencyMatrixForm codigoBanco={codigoBanco} fuente={fuente} onCambio={setEstadoFormulario} />

          {estadoFormulario && (
            <div className="fase2-preview-resumen">
              <p>
                {estadoFormulario.totalRespondidos} / {estadoFormulario.totalRequeridos} comportamientos
                respondidos{estadoFormulario.completo ? " — completo" : ""}.
              </p>

              {idAsignacion ? (
                <>
                  <button
                    type="button"
                    onClick={enviarCalificaciones}
                    disabled={envioEstado === "enviando" || estadoFormulario.totalRespondidos === 0}
                  >
                    {envioEstado === "enviando" ? "Guardando…" : "Guardar calificaciones respondidas"}
                  </button>
                  {envioMensaje && (
                    <pre className={`fase2-preview-envio-mensaje fase2-preview-envio-${envioEstado}`}>
                      {envioMensaje}
                    </pre>
                  )}
                </>
              ) : (
                <p className="fase2-preview-solo-vista">
                  Modo solo vista previa: no se está enviando nada al backend (no se indicó un id de
                  asignación).
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
